/**
 * @genesis_version V9.1
 * @genesis_hub The Network Transport & Auth Gateway
 * @description
 * Centralized HTTP client that injects Supabase access tokens into every outgoing API request.
 * Orchestrates token retrieval, URL construction, response parsing, 401 session purging,
 * and FormData file uploads — enforcing the "no raw fetch()" mandate across all frontend domains.
 * @blast_radius
 * - Downstream: Every TanStack Query hook, every domain service, all data fetching — the entire
 *   frontend data layer breaks if token injection or error handling is modified incorrectly.
 * - Upstream: Supabase auth session (token source), VITE_API_URL env variable (base URL).
 * @invariants
 * - Bearer token MUST be injected on every request (no anonymous API calls).
 * - 401 responses MUST trigger auth cache purge AND redirect (no silent failure).
 * - All network transport MUST route through apiRequest/apiBlobRequest/apiFormDataRequest (raw
 *   fetch() is a BLOCKING VIOLATION per Rule 4.5).
 */

import { supabase } from '@/core/api/supabase';
import type { ApiError } from '@shared/schemas';

/**
 * Centralized authenticated API request that returns the raw Response for
 * binary/blob downloads. Handles token injection and error parsing but
 * leaves the response body unconsumed so callers can call .blob(), .json(), etc.
 *
 * Use for: PDF/JSON-LD/XLSX file downloads where the response body must
 * be consumed as a Blob or ArrayBuffer, not parsed as JSON.
 */
export async function apiBlobRequest(
    endpoint: string,
    options: RequestInit & { signal?: AbortSignal } = {}
): Promise<Response> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
        throw new Error('Authentication required');
    }

    const method = (options.method ?? 'GET').toUpperCase();
    const hasBody = method === 'POST' || method === 'PUT' || method === 'PATCH';

    const response = await fetch(buildApiUrl(endpoint), {
        ...options,
        headers: {
            ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
            Authorization: `Bearer ${token}`,
            ...options.headers,
        },
    });

    if (!response.ok) {
        let errorMessage = `Request failed with status ${response.status}`;
        try {
            const errorBody = await response.json() as Record<string, unknown>;
            const serverError = errorBody?.error as Record<string, unknown> | undefined;
            if (serverError?.message) {
                errorMessage = String(serverError.message);
            } else if (typeof errorBody?.error === 'string') {
                errorMessage = errorBody.error as string;
            }
        } catch {
            // Response body is not JSON — use status-based message
        }
        throw new Error(errorMessage);
    }

    return response;
}

/**
 * Build a full API URL for the given endpoint
 */
function buildApiUrl(endpoint: string): string {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
    // Ensure base URL ends with /api if not already included
    const apiBase = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;

    // Safe URL concatenation: strip trailing slashes from apiBase and leading slashes from endpoint
    const cleanApiBase = apiBase.replace(/\/+$/, '');
    const cleanEndpoint = endpoint.replace(/^\/+/, '');

    return `${cleanApiBase}/${cleanEndpoint}`;
}

/**
 * Parse API response - returns data directly or throws error
 */
async function parseApiResponse<T = unknown>(response: Response, schema?: import('zod').ZodType<T>): Promise<T> {

    // 1. Read response as text FIRST (can only be read once)
    const text = await response.text();

    // 2. Safely parse JSON from text
    let data: unknown = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch (e) {
        // If not JSON, keep as text
        data = text;
    }

    // 3. Check for HTTP errors
    if (!response.ok) {
        // Handle 401 Unauthorized - clear auth cache and trigger logout
        if (response.status === 401) {
            // Synchronously nuke the auth cache. The ProtectedRoute will instantly bounce them to /login.
            const { queryClient } = await import('@/core/api/queryClient.js');
            queryClient.setQueryData(['auth', 'session'], null);
            queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
            throw new Error('Session expired. Please log in again.');
        }

        // Handle standardized Hono error envelope: { success: false, error: { code, message }, details? }
        const errorData = data as ApiError;
        const errorObj = errorData?.error;
        const fallbackMessage = `HTTP ${response.status}: ${response.statusText}`;
        let errorMessage: string;
        if (errorObj && typeof errorObj === 'object' && 'message' in errorObj) {
            errorMessage = typeof errorObj.message === 'string' ? errorObj.message : fallbackMessage;
        } else if (typeof errorObj === 'string') {
            errorMessage = errorObj;
        } else {
            errorMessage = (data as Record<string, unknown>)?.message as string | undefined ?? fallbackMessage;
        }
        throw new Error(errorMessage);
    }

    // 4. Validate or Return
    if (schema) return schema.parse(data);
    return data as T;
}

/**
 * Centralized authenticated API request with token caching
 * Handles token retrieval, injection, and response parsing
 */
export async function apiRequest<T = unknown>(
    endpoint: string,
    options: RequestInit & { signal?: AbortSignal } = {}
): Promise<T> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
        throw new Error('Authentication required');
    }

    const response = await fetch(buildApiUrl(endpoint), {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            ...options.headers,
        },
    });

    return await parseApiResponse<T>(response);
}

/**
 * Centralized authenticated FormData request for file uploads.
 * Does NOT set Content-Type — the browser auto-sets multipart/form-data with boundary.
 * V4.2: Extracted from FileIngestionDropzone raw fetch() to close the FormData gap.
 */
export async function apiFormDataRequest<T = unknown>(
    endpoint: string,
    formData: FormData,
    options: RequestInit & { signal?: AbortSignal } = {}
): Promise<T> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (!token) {
        throw new Error('Authentication required');
    }

    const response = await fetch(buildApiUrl(endpoint), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            ...options.headers,
        },
        body: formData,
        ...options,
    });

    return await parseApiResponse<T>(response);
}
